import { useState, useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CsrfContext } from '../../../../providers/CsrfProvider';
import { AuthContext } from '../../../auth/providers/AuthProvider';
import ApiClient from '../../../../services/ApiClient';
import NewUUID from '../../../../utils/randy/NewUUID';
import { useQueryClient } from 'react-query';
import { addTemplateParticipant, listTemplateParticipants, removeTemplateParticipant } from '../../../../services/template/Participants';
import { addNewStatement } from '../../../../services/template/Statements';
import { createGroup } from '../../../../services/group/Groups';
import { IsValidName } from '../../../../utils/Validators';
import { useGroup } from '../../../../providers/GroupProvider';
import { getBillRules, updateBillRule } from '../../../../services/bill/BillRules';

/**
 * useTemplateFlowManager - Custom hook for managing template creation flow state
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onStepTransition - Callback function called when step transitions occur
 * @param {Object} options.initialStepConfig - Initial step configuration
 * @returns {Object} template flow state and control functions
 */
export const useTemplateFlowManager = ({
    onStepTransition = () => {},
    initialStepConfig = {
        templateInfo: 'enabled',
        participants: 'hidden',
        initialStatement: 'hidden'
    }
} = {}) => {
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { selectedGroupId } = useGroup();

    // Core flow state
    const [currentStep, setCurrentStep] = useState('templateInfo');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [templateId, setTemplateId] = useState(null);

    // Form data state for each step
    const [templateInfoData, setTemplateInfoData] = useState({
        name: '',
        description: '',
        typical_cost: 0,
        group_id: null,
        new_group_name: ''
    });

    const [participantsData, setParticipantsData] = useState([]);
    const [statementData, setStatementData] = useState({
        cost: 0
    });

    // Set initial group to currently selected group from GroupProvider
    useEffect(() => {
        if (selectedGroupId) {
            setTemplateInfoData(prev => ({
                ...prev,
                group_id: selectedGroupId
            }));
        }
    }, [selectedGroupId]);

    // Step configuration state
    const [stepConfig, setStepConfig] = useState(initialStepConfig);

    // Step state management functions
    const setStateTemplateInfo = useCallback((state) => {
        setStepConfig(prev => ({ ...prev, templateInfo: state }));
    }, []);

    const setStateParticipants = useCallback((state) => {
        setStepConfig(prev => ({ ...prev, participants: state }));
    }, []);

    const setStateInitialStatement = useCallback((state) => {
        setStepConfig(prev => ({ ...prev, initialStatement: state }));
    }, []);

    // Helper functions for step configuration
    const isStepVisible = useCallback((stepName) => {
        return stepConfig[stepName] && (stepConfig[stepName] === 'enabled' || stepConfig[stepName] === 'disabled');
    }, [stepConfig]);

    const isStepEnabled = useCallback((stepName) => {
        return stepConfig[stepName] === 'enabled';
    }, [stepConfig]);

    const isStepDisabled = useCallback((stepName) => {
        return stepConfig[stepName] === 'disabled';
    }, [stepConfig]);

    // Step transition handler
    const handleStepTransition = useCallback((newStep, errorParam = null) => {
        setCurrentStep(newStep);
        setError(errorParam);
        setIsLoading(false);

        // Disable all currently enabled steps, then enable the new step
        setStepConfig(prev => {
            const newConfig = { ...prev };

            // Disable all currently enabled steps
            Object.keys(newConfig).forEach(step => {
                if (newConfig[step] === 'enabled') {
                    newConfig[step] = 'disabled';
                }
            });

            // Enable the new step
            if (newConfig[newStep] !== undefined) {
                newConfig[newStep] = 'enabled';
            }
            return newConfig;
        });

        onStepTransition(newStep, errorParam);
    }, [onStepTransition]);

    // template info handlers
    const handleTemplateInfoChange = useCallback((e) => {
        const { name, value } = e.target;
        let parsedValue = value;

        if (name === 'typical_cost') {
            parsedValue = parseFloat(value) || 0;
        } else if (name === 'group_id') {
            // Handle group_id - keep as string (UUID), can be null
            parsedValue = value === '' || value === null ? null : String(value);
        }

        setTemplateInfoData(prev => ({
            ...prev,
            [name]: parsedValue
        }));
    }, []);

    const handleTemplateInfoSubmit = useCallback(async () => {
        if (!templateInfoData.name.trim() || !templateInfoData.description.trim() || templateInfoData.typical_cost <= 0) {
            setError('Please fill in all required fields');
            return;
        }

        // Validate new group name if provided
        if (templateInfoData.new_group_name && templateInfoData.new_group_name.trim()) {
            if (!IsValidName(templateInfoData.new_group_name.trim())) {
                setError('Please provide a valid group name (max 50 characters)');
                return;
            }
        }

        setIsLoading(true);
        setError(null);

        try {
            let groupId = templateInfoData.group_id;
            
            // If new_group_name is provided, create the group first
            if (templateInfoData.new_group_name && templateInfoData.new_group_name.trim()) {
                try {
                    const newGroup = await createGroup(templateInfoData.new_group_name.trim(), csrfToken);
                    groupId = newGroup.id;
                    // Update templateInfoData with the new group_id so it's available for the participants step
                    setTemplateInfoData(prev => ({
                        ...prev,
                        group_id: newGroup.id,
                        new_group_name: '' // Clear the new group name
                    }));
                } catch (groupErr) {
                    console.error('Error creating group:', groupErr);
                    setError(groupErr.response?.data?.msg || 'Failed to create group. Please try again.');
                    setIsLoading(false);
                    return;
                }
            }
            
            const requestBody = {
                name: templateInfoData.name,
                description: templateInfoData.description,
                typical_cost: parseInt(templateInfoData.typical_cost)
            };
            
            // Include group_id if provided (must be UUID string)
            if (groupId) {
                // Ensure it's a string (UUID format)
                requestBody.group_id = String(groupId);
            }
            
            const response = await ApiClient.post('/api/bills/templates', requestBody, {
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    request_id: NewUUID(),
                    "X-CSRF-Token": csrfToken
                },
            });

            const newTemplateId = response.data.result.id;
            setTemplateId(newTemplateId);

            // If group_id was included in the response, update templateInfoData to ensure it's available for participants step
            if (response.data.result.group_id && !templateInfoData.group_id) {
                setTemplateInfoData(prev => ({
                    ...prev,
                    group_id: response.data.result.group_id
                }));
            }

            // Refresh templates list
            queryClient.invalidateQueries(['templates']);
            queryClient.refetchQueries(['templates'], { active: true, stale: false });

            // Transition to participants step
            handleStepTransition('participants');
        } catch (err) {
            console.error('Error creating template:', err);
            setError(err.response?.data?.msg || 'Failed to create template. Please try again.');
            setIsLoading(false);
        }
    }, [templateInfoData, csrfToken, queryClient, handleStepTransition]);

    // Participants handlers
    const handleAddParticipant = useCallback(async (formData) => {
        if (!templateId) {
            setError('template ID is missing');
            return;
        }

        if (!formData.email || !formData.email.trim()) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const addParticipant = addTemplateParticipant(csrfToken);
            formData.templateId = templateId;
            const newParticipant = await addParticipant(formData);

            // Refresh participants list
            queryClient.invalidateQueries(['participants', String(templateId)]);

            // Update local state
            setParticipantsData(prev => [...prev, formData.email]);

            // Automatically add the new participant to the default rule
            try {
                // Fetch current rules to find the default rule
                const rules = await getBillRules(String(templateId), csrfToken);
                const defaultRule = rules.find(rule => rule.is_default);

                if (defaultRule) {
                    // Get all current participants including the newly added one
                    // Use the query client to fetch fresh data
                    const allParticipants = await queryClient.fetchQuery(
                        ['participants', String(templateId)],
                        listTemplateParticipants(csrfToken),
                        { staleTime: 0 }
                    );
                    
                    // Get participant user IDs (filter out any that don't have IDs yet)
                    const participantUserIDs = (allParticipants || [])
                        .filter(p => p && p.id)
                        .map(p => p.id);

                    if (participantUserIDs.length > 0) {
                        // Calculate even split percentage
                        const equalPercent = 100 / participantUserIDs.length;
                        const percentages = {};
                        participantUserIDs.forEach(id => {
                            percentages[id] = equalPercent;
                        });

                        // Update the default rule to include all participants with even split
                        const ruleData = {
                            rule_type: 'evenly',
                            participant_user_ids: participantUserIDs,
                            participant_percentages: percentages,
                        };

                        await updateBillRule(String(templateId), defaultRule.id, ruleData, csrfToken);
                        
                        // Refetch rules query to refresh the display immediately
                        await queryClient.refetchQueries(['billRules', String(templateId)]);
                        await queryClient.refetchQueries(['estimatedSplits', String(templateId)]);
                    }
                }
            } catch (ruleErr) {
                // Log error but don't fail the participant addition
                console.error('Error updating default rule:', ruleErr);
            }

            setIsLoading(false);
        } catch (err) {
            console.error('Error adding participant:', err);
            setError(err.response?.data?.msg || 'Failed to add participant. Please try again.');
            setIsLoading(false);
        }
    }, [templateId, csrfToken, queryClient]);

    const handleRemoveParticipant = useCallback(async (participant) => {
        if (!templateId) {
            setError('template ID is missing');
            return;
        }

        if (!participant || !participant.email) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const removeParticipant = removeTemplateParticipant(csrfToken);
            await removeParticipant({
                templateId: templateId,
                email: participant.email
            });

            // Refresh participants list
            queryClient.invalidateQueries(['participants', String(templateId)]);
            queryClient.invalidateQueries(['groupParticipantsExcludingBill', templateInfoData.group_id, templateId]);

            // Update local state
            setParticipantsData(prev => prev.filter(p => {
                const pEmail = typeof p === 'string' ? p : p.email;
                return pEmail !== participant.email;
            }));

            // Automatically update the default rule to exclude the removed participant
            try {
                // Fetch current rules to find the default rule
                const rules = await getBillRules(String(templateId), csrfToken);
                const defaultRule = rules.find(rule => rule.is_default);

                if (defaultRule) {
                    // Get all remaining participants after removal
                    const allParticipants = await queryClient.fetchQuery(
                        ['participants', String(templateId)],
                        listTemplateParticipants(csrfToken),
                        { staleTime: 0 }
                    );
                    
                    // Get participant user IDs (filter out any that don't have IDs yet)
                    const participantUserIDs = (allParticipants || [])
                        .filter(p => p && p.id)
                        .map(p => p.id);

                    if (participantUserIDs.length > 0) {
                        // Calculate even split percentage
                        const equalPercent = 100 / participantUserIDs.length;
                        const percentages = {};
                        participantUserIDs.forEach(id => {
                            percentages[id] = equalPercent;
                        });

                        // Update the default rule to include only remaining participants with even split
                        const ruleData = {
                            rule_type: 'evenly',
                            participant_user_ids: participantUserIDs,
                            participant_percentages: percentages,
                        };

                        await updateBillRule(String(templateId), defaultRule.id, ruleData, csrfToken);
                        
                        // Refetch rules query to refresh the display immediately
                        await queryClient.refetchQueries(['billRules', String(templateId)]);
                        await queryClient.refetchQueries(['estimatedSplits', String(templateId)]);
                    } else {
                        // If no participants left, refetch to show empty state
                        await queryClient.refetchQueries(['billRules', String(templateId)]);
                        await queryClient.refetchQueries(['estimatedSplits', String(templateId)]);
                    }
                }
            } catch (ruleErr) {
                // Log error but don't fail the participant removal
                console.error('Error updating default rule after participant removal:', ruleErr);
            }

            setIsLoading(false);
        } catch (err) {
            console.error('Error removing participant:', err);
            setError(err.response?.data?.msg || 'Failed to remove participant. Please try again.');
            setIsLoading(false);
        }
    }, [templateId, csrfToken, queryClient, templateInfoData.group_id]);

    const handleFlowComplete = useCallback(() => {
        setIsLoading(false);
        // Navigate to the template view
        if (templateId) {
            navigate(`/bills/templates/${templateId}`);
        }
    }, [templateId, history]);

    const handleParticipantsContinue = useCallback(async () => {
        // Check if we have participants - if not, skip to completion
        if (!templateId) {
            handleFlowComplete();
            return;
        }

        try {
            // Refetch participants to get latest count
            const latestParticipants = await queryClient.fetchQuery(
                ['participants', String(templateId)],
                listTemplateParticipants(csrfToken),
                { staleTime: 0 }
            );

            const hasParticipants = latestParticipants && Array.isArray(latestParticipants) && latestParticipants.length > 0;

            if (hasParticipants) {
                // Transition to initial statement step
                handleStepTransition('initialStatement');
            } else {
                // Skip all remaining steps and complete flow
                handleFlowComplete();
            }
        } catch (error) {
            // If error fetching, assume no participants and complete flow
            console.error('Error fetching participants:', error);
            handleFlowComplete();
        }
    }, [templateId, csrfToken, queryClient, handleStepTransition, handleFlowComplete]);

    // Initial statement handlers
    const handleStatementChange = useCallback((e) => {
        const { name, value } = e.target;
        const parsedValue = parseFloat(value) || 0;

        setStatementData(prev => ({
            ...prev,
            [name]: parsedValue
        }));
    }, []);

    const handleStatementSubmit = useCallback(async () => {
        if (!templateId) {
            setError('template ID is missing');
            return;
        }

        if (statementData.cost <= 0) {
            // User can skip initial statement, just continue
            handleFlowComplete();
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const addStatement = addNewStatement(csrfToken);
            const formData = {
                templateId: templateId,
                cost: statementData.cost
            };
            await addStatement(formData);

            // Refresh statements and rule history
            queryClient.invalidateQueries(['statements', String(templateId)]);
            queryClient.invalidateQueries(['ruleHistory', String(templateId)]);

            handleFlowComplete();
        } catch (err) {
            console.error('Error creating initial statement:', err);
            setError(err.response?.data?.msg || 'Failed to create initial statement. Please try again.');
            setIsLoading(false);
        }
    }, [templateId, statementData, csrfToken, queryClient, handleFlowComplete]);

    const handleSkipStatement = useCallback(() => {
        handleFlowComplete();
    }, [handleFlowComplete]);

    // Get step data
    const getStepData = useCallback((stepName) => {
        switch (stepName) {
            case 'templateInfo':
                return {
                    formData: templateInfoData,
                    onChange: handleTemplateInfoChange,
                    onSubmit: handleTemplateInfoSubmit
                };
            case 'participants':
                return {
                    templateId: templateId,
                    participants: participantsData,
                    onAddParticipant: handleAddParticipant,
                    onRemoveParticipant: handleRemoveParticipant,
                    onContinue: handleParticipantsContinue,
                    groupId: templateInfoData.group_id
                };
            case 'initialStatement':
                return {
                    formData: statementData,
                    onChange: handleStatementChange,
                    onSubmit: handleStatementSubmit,
                    onSkip: handleSkipStatement
                };
            default:
                return null;
        }
    }, [templateInfoData, handleTemplateInfoChange, handleTemplateInfoSubmit,
        templateId, participantsData, handleAddParticipant, handleRemoveParticipant, handleParticipantsContinue,
        statementData, handleStatementChange, handleStatementSubmit, handleSkipStatement]);

    return {
        // Current state
        currentStep,
        isLoading,
        error,
        templateId,

        // Step configuration
        stepConfig,
        isStepVisible,
        isStepEnabled,
        isStepDisabled,

        // Step data
        getStepData,

        // Handlers
        handleStepTransition,
        setError,
        setIsLoading
    };
};
